import type { ListBuilderConfiguration } from '../types';

/**
 * Sensor Reading Embedded list — historical readings for a single sensor.
 * Rendered inside `/locations/:id/sensors/:sensorId/historical-data` and
 * fed by `SensorsService.getReadings` via wrapQueryService. The frontend
 * adapter injects derived `distanceInches` and `moduleTemperatureFahrenheit`
 * fields on each row so the columns display U.S. customary units even
 * though the database stores metric values.
 */
export const SENSOR_READING_SENSOR_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-sensor-readings-sensor-embedded',
			cacheTTL: 60000,
			name: 'Historical Readings',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'timestamp',
					fields: [
						{
							id: 'timestamp-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'createdDate' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: {
									dateFormat: 'datetime-medium',
									showTimeIfSet: true,
								},
							},
							displayStyle: 'datetime',
						},
					],
					headers: [{ id: 'h-timestamp', text: 'Date / Time' }],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [{ direction: 'DESC', fieldName: 'createdDate' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'distance',
					fields: [
						{
							id: 'distance-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'distanceInches' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-distance', text: 'Distance (in)' }],
					minWidth: 110,
					sortable: true,
					alignment: 'right',
					sortFields: [{ direction: 'ASC', fieldName: 'distance' }],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'temperature',
					fields: [
						{
							id: 'temperature-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'moduleTemperatureFahrenheit' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-temp', text: 'Temp (°F)' }],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [{ direction: 'ASC', fieldName: 'moduleTemperature' }],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'battery-level',
					fields: [
						{
							id: 'battery-level-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'batteryLevel' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-battery', text: 'Battery (%)' }],
					minWidth: 110,
					sortable: true,
					alignment: 'right',
					sortFields: [{ direction: 'DESC', fieldName: 'batteryLevel' }],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'cellular-signal',
					fields: [
						{
							id: 'cellular-signal-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'cellularSignalStrength' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-cellular', text: 'Cellular SS (dBm)' }],
					minWidth: 140,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{ direction: 'DESC', fieldName: 'cellularSignalStrength' },
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'module-signal',
					fields: [
						{
							id: 'module-signal-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'moduleSignalStrength' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-module', text: 'Module SS' }],
					minWidth: 110,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{ direction: 'DESC', fieldName: 'moduleSignalStrength' },
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
			],
			description: 'Historical readings for this sensor',
			defaultSortId: 'newest-first',
			requiredFields: [
				'sensorReading.id',
				'sensorReading.createdDate',
				'sensorReading.distance',
				'sensorReading.moduleTemperature',
				'sensorReading.batteryLevel',
				'sensorReading.cellularSignalStrength',
				'sensorReading.moduleSignalStrength',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 250],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/sensor-readings/:id',
			objectLabel: 'Sensor Reading',
			objectTable: 'sensor_reading',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'createdDate', fieldLabel: 'Timestamp' },
				{ fieldName: 'distance', fieldLabel: 'Distance' },
				{ fieldName: 'moduleTemperature', fieldLabel: 'Module Temperature' },
				{ fieldName: 'batteryLevel', fieldLabel: 'Battery Level' },
				{
					fieldName: 'cellularSignalStrength',
					fieldLabel: 'Cellular Signal',
				},
				{
					fieldName: 'moduleSignalStrength',
					fieldLabel: 'Module Signal',
				},
			],
		},
	],
	tableSorts: [
		{
			id: 'newest-first',
			name: 'Newest First',
			fields: [{ direction: 'DESC', fieldName: 'createdDate' }],
			description: 'Show the most recent readings first',
		},
		{
			id: 'oldest-first',
			name: 'Oldest First',
			fields: [{ direction: 'ASC', fieldName: 'createdDate' }],
			description: 'Show the oldest readings first',
		},
	],
};
